/* SilcoCarousel — Alpine component factory shared by the SILCO carousel
   sections (Featured practices, Latest editorial).
   Scroll-snap based: native touch swipe + wheel, plus mouse drag, arrow
   buttons, numbered pagination and keyboard support. No global state —
   every x-data instance is isolated, so multiple sections per page and
   Theme Editor re-renders (Alpine re-initialises injected DOM) are safe. */
(function () {
  if (window.SilcoCarousel) return;

  window.SilcoCarousel = function (opts) {
    opts = opts || {};
    return {
      index: 0,
      total: 0,
      atStart: true,
      atEnd: false,
      _dragging: false,
      _moved: false,
      _startX: 0,
      _startLeft: 0,
      _raf: null,
      // Autoplay (opt-in per section): auto-advance every `interval` seconds
      // with a SEAMLESS forward loop — a clone of slide 1 sits after the last
      // slide, we glide onto it, then jump instantly (invisibly) back to the
      // real slide 1. Paused on hover/drag/hidden tab; disabled entirely for
      // prefers-reduced-motion users.
      _autoplay: !!opts.autoplay,
      _interval: Math.max(2, Number(opts.interval) || 3) * 1000,
      _cloned: false,
      _paused: false,
      _jumping: false,
      _timer: null,

      mounted() {
        // Legacy/fallback renders (e.g. Conversation single-article mode) have
        // no track — the component is inert there.
        if (!this.$refs.track) return;
        this.refresh();
        // Keep state correct when slides resize (responsive breakpoints).
        this._ro = new ResizeObserver(() => this.update());
        this._ro.observe(this.track());

        var reduced =
          window.matchMedia &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (this._autoplay && !reduced && this.total > 1) {
          this._setupLoop();
          var track = this.track();
          track.addEventListener('mouseenter', () => (this._paused = true));
          track.addEventListener('mouseleave', () => (this._paused = false));
          this._start();
        }
      },

      destroy() {
        if (this._ro) this._ro.disconnect();
        if (this._timer) clearInterval(this._timer);
      },

      _setupLoop() {
        if (this._cloned) return;
        var first = this.slides()[0];
        if (!first) return;
        var clone = first.cloneNode(true);
        clone.setAttribute('data-silco-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        this.track().appendChild(clone);
        this._cloned = true;
      },

      _start() {
        if (this._timer) clearInterval(this._timer);
        this._timer = setInterval(() => {
          if (this._paused || this._dragging || this._jumping || document.hidden) return;
          this.next();
        }, this._interval);
      },

      /** Glide onto the clone, then snap invisibly back to the real slide 1. */
      _toCloneThenStart() {
        var track = this.track();
        var all = this.allSlides();
        var clone = all[this.total];
        if (!clone) return;
        this._jumping = true;
        track.scrollTo({
          left: clone.offsetLeft - all[0].offsetLeft,
          behavior: 'smooth',
        });
        var self = this;
        var fb = null;
        var done = function () {
          track.removeEventListener('scrollend', done);
          if (fb) clearTimeout(fb);
          if (!self._jumping) return;
          self._jumping = false;
          var prevBehavior = track.style.scrollBehavior;
          track.style.scrollBehavior = 'auto';
          track.scrollLeft = 0;
          track.style.scrollBehavior = prevBehavior;
          self.update();
        };
        track.addEventListener('scrollend', done, { once: true });
        fb = setTimeout(done, 900);
      },

      track() {
        return this.$refs.track;
      },

      allSlides() {
        return Array.from(this.track().children);
      },

      /** Real slides only — the loop clone never counts for pagination. */
      slides() {
        return this.allSlides().filter(function (el) {
          return !el.hasAttribute('data-silco-clone');
        });
      },

      refresh() {
        this.total = this.slides().length;
        this.update();
      },

      update() {
        const track = this.track();
        const slides = this.allSlides();
        if (!slides.length) return;
        const mid = track.scrollLeft + 1;
        let idx = 0;
        for (let i = 0; i < slides.length; i++) {
          if (slides[i].offsetLeft - slides[0].offsetLeft <= mid) idx = i;
        }
        this.index = this.total > 0 ? idx % this.total : 0;
        this.atStart = track.scrollLeft <= 1;
        this.atEnd = this._cloned
          ? false
          : track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
      },

      onScroll() {
        if (this._raf) cancelAnimationFrame(this._raf);
        this._raf = requestAnimationFrame(() => this.update());
      },

      goTo(i) {
        const slides = this.allSlides();
        if (!slides.length) return;
        const clamped = Math.max(0, Math.min(i, slides.length - 1));
        const track = this.track();
        track.scrollTo({
          left: slides[clamped].offsetLeft - slides[0].offsetLeft,
          behavior: 'smooth',
        });
      },

      next() {
        if (this._cloned && this.index >= this.total - 1) {
          // Seamless wrap: forward past the last slide onto the clone of #1.
          this._toCloneThenStart();
          return;
        }
        if (this.atEnd) return;
        this.goTo(this.index + 1);
      },

      prev() {
        this.goTo(this.index - 1);
      },

      onKeydown(event) {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.next();
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.prev();
        }
      },

      // Mouse drag (touch uses native scrolling).
      onPointerdown(event) {
        if (event.pointerType !== 'mouse') return;
        this._dragging = true;
        this._moved = false;
        this._startX = event.clientX;
        this._startLeft = this.track().scrollLeft;
      },

      onPointermove(event) {
        if (!this._dragging) return;
        const dx = event.clientX - this._startX;
        if (Math.abs(dx) > 4 && !this._moved) {
          this._moved = true;
          this.track().classList.add('is-dragging');
        }
        if (this._moved) this.track().scrollLeft = this._startLeft - dx;
      },

      onPointerup() {
        if (!this._dragging) return;
        this._dragging = false;
        if (this._moved) {
          const track = this.track();
          track.classList.remove('is-dragging');
          // Settle onto the nearest snap point.
          this.goTo(this.nearestIndex());
        }
      },

      nearestIndex() {
        const track = this.track();
        const slides = this.allSlides();
        let best = 0;
        let bestDist = Infinity;
        slides.forEach((slide, i) => {
          const dist = Math.abs(slide.offsetLeft - slides[0].offsetLeft - track.scrollLeft);
          if (dist < bestDist) {
            bestDist = dist;
            best = i;
          }
        });
        return best;
      },

      // Suppress the click that follows a drag so cards don't navigate.
      onClickCapture(event) {
        if (this._moved) {
          event.preventDefault();
          event.stopPropagation();
          this._moved = false;
        }
      },
    };
  };
})();
